import React, { useState, useMemo, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Tooltip,
  Space,
  Spin
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Line } from "@ant-design/plots";
import moment from "moment";
import axios from "axios";
import ViewTypeSelector from "../AdminThroughputCharts/ViewTypeSelector";
import DateRangePicker from "../../DateRangePicker/DateRangePicker";
import ENVIRONMENT from "../../../../environments";

const { Title } = Typography;
const DATE_FORMAT = "YYYY/MM/DD";

const ConversionsChart = ({ chartStyle, dateRange, onDateChange }) => {
  const [viewType, setViewType] = useState("Daily");
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch trial to paid conversions data
  const fetchConversionsData = async () => {
    if (!dateRange?.start || !dateRange?.end) return;
    
    try {
      setIsLoading(true);
      const payload = {
        action: "get_trial_to_paid_user_count",
        start_date: moment(dateRange.start).format("YYYY-MM-DD"),
        end_date: moment(dateRange.end).format("YYYY-MM-DD")
      };

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      const data = response.data || [];
      
      // Transform API data to chart format
      const transformedData = data.map(item => ({
        date: moment(item.date, 'YYYY-MM-DD').format('YYYY/MM/DD'),
        conversions: parseInt(item.count, 10),
        type: 'Conversions'
      }));

      setChartData(transformedData);
    } catch (error) {
      console.error("Error fetching conversions data:", error);
      setChartData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when date range changes
  useEffect(() => {
    fetchConversionsData();
  }, [dateRange]);

  // Process data based on view type
  const processedData = useMemo(() => {
    if (viewType === "Daily") {
      return chartData;
    }

    if (viewType === "Weekly") {
      const weeklyData = new Map();
      
      chartData.forEach((item) => {
        const itemDate = moment(item.date, 'YYYY/MM/DD');
        const weekStart = itemDate.clone().startOf('week');
        const weekEnd = itemDate.clone().endOf('week');
        const weekLabel = `${weekStart.format('MMM DD')} - ${weekEnd.format('MMM DD')}`;
        const weekKey = weekStart.format('YYYY/MM/DD');
        
        if (!weeklyData.has(weekKey)) {
          weeklyData.set(weekKey, {
            date: weekLabel,
            conversions: 0,
            type: 'Conversions',
            sortDate: weekStart.format('YYYY/MM/DD')
          });
        }
        weeklyData.get(weekKey).conversions += item.conversions;
      });
      
      return Array.from(weeklyData.values()).sort((a, b) => 
        moment(a.sortDate, 'YYYY/MM/DD').diff(moment(b.sortDate, 'YYYY/MM/DD'))
      );
    }

    if (viewType === "Monthly") {
      const monthlyData = new Map();
      
      chartData.forEach((item) => {
        const itemDate = moment(item.date, 'YYYY/MM/DD');
        const monthStart = itemDate.clone().startOf('month');
        const monthLabel = monthStart.format('MMM YYYY');
        const monthKey = monthStart.format('YYYY/MM');
        
        if (!monthlyData.has(monthKey)) {
          monthlyData.set(monthKey, {
            date: monthLabel,
            conversions: 0,
            type: 'Conversions',
            sortDate: monthStart.format('YYYY/MM/DD')
          });
        }
        monthlyData.get(monthKey).conversions += item.conversions;
      });
      
      return Array.from(monthlyData.values()).sort((a, b) => 
        moment(a.sortDate, 'YYYY/MM/DD').diff(moment(b.sortDate, 'YYYY/MM/DD'))
      );
    }

    return chartData;
  }, [chartData, viewType]);

  const chartConfig = useMemo(() => {
    return {
      data: processedData,
      xField: 'date',
      yField: 'conversions',
      smooth: true,
      animation: false,
      color: '#52c41a',
      point: {
        size: 4,
        shape: 'circle',
        style: {
          fill: '#52c41a',
          stroke: '#fff',
          lineWidth: 2,
        },
      },
      lineStyle: {
        lineWidth: 2,
      },
      xAxis: {
        label: {
          autoRotate: true,
          autoHide: true,
        },
      },
      yAxis: {
        label: {
          formatter: (v) => `${v}`,
        },
        title: {
          text: 'Conversions',
        },
      },
      tooltip: {
        formatter: (datum) => {
          return {
            name: 'Conversions',
            value: datum.conversions,
          };
        },
      },
    };
  }, [processedData]);

  return (
    <Col span={24}>
      <Card style={chartStyle}>
        <Row justify="space-between" align="middle">
          <Title level={4}>
            Trial to Paid Conversions{' '}
            <Tooltip title="Daily, weekly, or monthly count of free trial users converting to paid subscriptions">
              <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </Title>
          <Row gutter={12}>
            <Col>
              <Space>
                <DateRangePicker
                  start={dateRange.start}
                  end={dateRange.end}
                  date_format={DATE_FORMAT}
                  onCalChange={onDateChange}
                />
                <ViewTypeSelector
                  value={viewType}
                  onChange={setViewType}
                />
              </Space>
            </Col>
          </Row>
        </Row>

        <Divider style={{ margin: "16px 0" }} />
        <Spin spinning={isLoading}>
          <Line {...chartConfig} />
        </Spin>
      </Card>
    </Col>
  );
};

export default ConversionsChart;
